import { createFileRoute } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { Info, Plus, RefreshCw } from 'lucide-react';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { EmptyState } from '@/components/ui/EmptyState';
import { ErrorAlert } from '@/components/ui/ErrorAlert';
import { DataTable } from '@/components/ui/DataTable';
import type { Column } from '@/components/ui/DataTable';
import { SkeletonRows } from '@/components/ui/Skeleton';
import { api } from '@/lib/api';
import { formatDate } from '@/lib/format';

/**
 * Service-Type-Katalog (Sprint-1-Carry-Over).
 *
 * Backend-Contract (GET /api/v1/service-types, wird separat implementiert):
 *   { data: [{ id, code, display_name, category, description,
 *              eol_date, is_active }], total }
 *
 * Wichtig: Es gibt (noch) KEINE Admin-Schreib-Endpunkte (Create/Update) und
 * kein Unbekannte-Strings-Mapping im Backend. Solche Aktionen sind hier daher
 * als "noch nicht verfuegbar" gekennzeichnet statt kaputte Calls abzusetzen.
 *
 * Dieser Katalog-Query ist bewusst lokal (eigener Wire-Typ + eigener
 * Query-Key), damit er unabhängig vom generischen useServiceTypes-Hook exakt
 * gegen den neuen {data,total}-Contract läuft.
 */

interface ServiceTypeCatalogItem {
  id: string;
  code: string;
  display_name: string;
  category: string;
  description: string | null;
  eol_date: string | null;
  is_active: boolean;
}

interface ServiceTypeCatalogResponse {
  data: ServiceTypeCatalogItem[];
  total: number;
}

function useServiceTypeCatalog(): ReturnType<
  typeof useQuery<ServiceTypeCatalogResponse>
> {
  return useQuery<ServiceTypeCatalogResponse>({
    queryKey: ['service-types', 'catalog'],
    queryFn: () => api.get<ServiceTypeCatalogResponse>('/service-types'),
    staleTime: 15 * 60 * 1000,
  });
}

export const Route = createFileRoute('/settings/service-types')({
  component: ServiceTypesPage,
});

function ServiceTypesPage(): JSX.Element {
  const catalog = useServiceTypeCatalog();
  const items = catalog.data?.data ?? [];

  return (
    <div className="flex flex-col gap-4">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold">Service-Types</h1>
          <p className="text-muted">
            Katalog der unterstützten Service-Stacks (Anzeigename, Kategorie,
            Code, EOL-Datum).
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="secondary"
            leftIcon={<RefreshCw className="size-4" />}
            onClick={() => void catalog.refetch()}
            loading={catalog.isFetching}
          >
            Aktualisieren
          </Button>
          {/* Schreib-Endpunkt existiert im Backend noch nicht -> disabled. */}
          <Button
            leftIcon={<Plus className="size-4" />}
            disabled
            title="Anlegen ist noch nicht verfügbar - der Backend-Endpunkt fehlt."
          >
            Service-Type anlegen
          </Button>
        </div>
      </header>

      <Card
        title={
          <span className="flex items-center gap-2">
            <span>Registrierte Service-Types</span>
            {catalog.data && <Badge tone="neutral">{catalog.data.total}</Badge>}
          </span>
        }
      >
        {catalog.isLoading && <SkeletonRows rows={5} cols={5} />}
        {catalog.isError && <ErrorAlert error={catalog.error} />}
        {!catalog.isLoading && !catalog.isError && items.length === 0 && (
          <EmptyState
            icon={<Info className="size-8" />}
            title="Noch keine Service-Types"
            description="Der Katalog ist leer. Sobald das Backend Service-Types liefert, erscheinen sie hier."
          />
        )}
        {items.length > 0 && (
          <DataTable
            caption="Service-Types"
            columns={SERVICE_TYPE_COLUMNS}
            rows={items}
            rowKey={(t) => t.id}
            storageKey="service-types"
          />
        )}
      </Card>

      <Card title="Unbekannte Service-Strings">
        <div
          className="flex items-start gap-2 rounded-md border border-border bg-surface-2 p-3 text-sm text-muted"
          data-testid="service-types-mapping-unavailable"
        >
          <Info className="mt-0.5 size-4 shrink-0" aria-hidden="true" />
          <p>
            Das Zuordnen unbekannter Strings aus ods-Imports ist noch nicht
            verfügbar - der zugehörige Backend-Endpunkt fehlt aktuell. Die
            Funktion wird nachgereicht, sobald der Admin-Teil des
            Service-Type-Endpunkts steht.
          </p>
        </div>
      </Card>
    </div>
  );
}

const SERVICE_TYPE_COLUMNS: ReadonlyArray<Column<ServiceTypeCatalogItem>> = [
  { id: 'name', header: 'Anzeigename', className: 'font-medium', cell: (t) => t.display_name },
  {
    id: 'kategorie',
    header: 'Kategorie',
    filter: 'select',
    filterValue: (t) => t.category,
    cell: (t) => <Badge tone="neutral">{t.category}</Badge>,
  },
  { id: 'code', header: 'Code', className: 'font-mono text-xs', cell: (t) => t.code },
  {
    id: 'eol-datum',
    header: 'EOL-Datum',
    className: 'text-xs',
    filterValue: (t) => {
      if (!t.eol_date) return '';
      const label = formatDate(t.eol_date);
      return label === '–' ? '' : label;
    },
    cell: (t) => (t.eol_date ? formatDate(t.eol_date) : '–'),
  },
  {
    id: 'status',
    header: 'Status',
    filter: 'select',
    filterValue: (t) => (t.is_active ? 'aktiv' : 'inaktiv'),
    cell: (t) =>
      t.is_active ? <Badge tone="success">aktiv</Badge> : <Badge tone="neutral">inaktiv</Badge>,
  },
  {
    id: 'beschreibung',
    header: 'Beschreibung',
    className: 'text-muted text-xs',
    filterValue: (t) => t.description ?? '',
    cell: (t) => t.description ?? '–',
  },
];
